import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect, Fragment } from 'react'
import { useEvento, useUpdateEvento, useDeleteEvento, useTiposAnomalia, useEventoHistorial, useEventos } from '../hooks/useEventos'
import { useFiltersStore } from '../store/filtersStore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getImagenesEvento, getImagenCompleta } from '../api/imagenes'
import { getComentarios, createComentario } from '../api/comentarios'
import confetti from 'canvas-confetti'
import {
  ESTADOS,
  ESTADOS_LABELS,
  SEVERIDADES,
  SEVERIDADES_LABELS,
  SEVERIDADES_DOT_COLORS,
  SEVERIDADES_HEADER_GRADIENTS
} from '../utils/constants'
import { formatDate, formatDuration, parseUtcDate } from '../utils/formatters'
import { addBase64Prefix, getMimeTypeFromExtension } from '../utils/imageHelpers'
import { Dialog, Transition, Listbox } from '@headlessui/react'
import {
  BellAlertIcon,
  EyeIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  PhotoIcon,
  ChatBubbleLeftIcon,
  MapPinIcon,
  UserCircleIcon,
  CalendarIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon
} from '@heroicons/react/24/outline'
import Loading from '../components/common/Loading'
import TipoAnomaliaIcon from '../components/common/TipoAnomaliaIcon'
import toast from 'react-hot-toast'

// Componente para el selector de estado con iconos
const EstadoSelector = ({ value, onChange, disabled }) => {
  const estadosConfig = {
    [ESTADOS.NUEVO]: { label: 'Nuevo', icon: BellAlertIcon, color: 'text-blue-600 bg-blue-50 border-blue-200' },
    [ESTADOS.VISTO]: { label: 'Visto', icon: EyeIcon, color: 'text-purple-600 bg-purple-50 border-purple-200' },
    [ESTADOS.EN_PROCESO]: { label: 'En Proceso', icon: WrenchScrewdriverIcon, color: 'text-orange-600 bg-orange-50 border-orange-200' },
    [ESTADOS.RESUELTO]: { label: 'Resuelto', icon: CheckCircleIcon, color: 'text-green-600 bg-green-50 border-green-200' },
    [ESTADOS.DESCARTADO]: { label: 'Descartado', icon: XCircleIcon, color: 'text-gray-600 bg-gray-50 border-gray-200' },
  }

  const estadoActual = estadosConfig[value]
  const IconoActual = estadoActual.icon

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className="relative">
        <Listbox.Button className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-medium transition-all hover:shadow-md ${estadoActual.color}`}>
          <IconoActual className="w-5 h-5" />
          <span>{estadoActual.label}</span>
          <ChevronDownIcon className="w-4 h-4" />
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            <div className="p-2">
              {Object.entries(estadosConfig).map(([key, config]) => {
                const Icon = config.icon
                return (
                  <Listbox.Option
                    key={key}
                    value={parseInt(key)}
                    className={({ active }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-md cursor-pointer transition-colors ${
                        active ? 'bg-gray-100' : ''
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <Icon className={`w-5 h-5 ${config.color.split(' ')[0]}`} />
                        <span className={`flex-1 text-gray-900 ${selected ? 'font-semibold' : 'font-normal'}`}>
                          {config.label}
                        </span>
                        {selected && <CheckIcon className="w-5 h-5 text-primary" />}
                      </>
                    )}
                  </Listbox.Option>
                )
              })}
            </div>
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}

// Componente para el selector de gravedad con puntos de color
const GravedadSelector = ({ value, onChange, disabled }) => {
  const gravedadConfig = {
    [SEVERIDADES.BAJA]: { label: 'Baja', color: SEVERIDADES_DOT_COLORS[SEVERIDADES.BAJA] },
    [SEVERIDADES.MEDIA]: { label: 'Media', color: SEVERIDADES_DOT_COLORS[SEVERIDADES.MEDIA] },
    [SEVERIDADES.ALTA]: { label: 'Alta', color: SEVERIDADES_DOT_COLORS[SEVERIDADES.ALTA] },
  }

  const gravedadActual = gravedadConfig[value]

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <>
          <div className="relative">
            <Listbox.Button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-gray-200 bg-white font-medium transition-all hover:shadow-md hover:border-gray-300">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: gravedadActual.color }} />
              <span>{gravedadActual.label}</span>
              <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            </Listbox.Button>
          </div>
          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute mt-2 w-40 rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
              <div className="p-2">
                {Object.entries(gravedadConfig).map(([key, config]) => (
                  <Listbox.Option
                    key={key}
                    value={parseInt(key)}
                    className={({ active }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-md cursor-pointer transition-colors ${
                        active ? 'bg-gray-100' : ''
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                        <span className={`flex-1 text-gray-900 ${selected ? 'font-semibold' : 'font-normal'}`}>
                          {config.label}
                        </span>
                        {selected && <CheckIcon className="w-5 h-5 text-primary" />}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </div>
            </Listbox.Options>
          </Transition>
        </>
      )}
    </Listbox>
  )
}

// Componente para el selector de tipo de anomalía
const TipoAnomaliaSelector = ({ value, onChange, disabled, tiposAnomalia }) => {
  const tipoActual = tiposAnomalia?.find(t => t.idTipoAnomalia === value)

  if (!tiposAnomalia || tiposAnomalia.length === 0) {
    return <div className="text-3xl font-bold text-gray-900">{tipoActual?.nombre || 'Cargando...'}</div>
  }

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className="relative">
        <Listbox.Button className="group inline-flex items-center gap-3 text-left transition-colors">
          {tipoActual?.iconoUrl && (
            <TipoAnomaliaIcon
              iconoUrl={tipoActual.iconoUrl}
              color={tipoActual.colorIdentificacion}
              className="w-8 h-8 flex-shrink-0 drop-shadow-lg"
              alt={tipoActual.nombre}
            />
          )}
          <h1 className="text-3xl font-bold text-white group-hover:text-white/90 transition-colors">
            {tipoActual?.nombre || 'Seleccionar tipo'}
          </h1>
          <PencilIcon className="w-6 h-6 text-white/80 group-hover:text-white transition-colors" />
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute left-0 mt-2 w-80 max-h-60 overflow-auto origin-top-left rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            <div className="p-2">
              {tiposAnomalia.map((tipo) => (
                <Listbox.Option
                  key={tipo.idTipoAnomalia}
                  value={tipo.idTipoAnomalia}
                  className={({ active }) =>
                    `px-4 py-3 rounded-md cursor-pointer transition-colors ${
                      active ? 'bg-gray-100' : ''
                    }`
                  }
                >
                  {({ selected }) => (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1">
                        {tipo.iconoUrl && (
                          <TipoAnomaliaIcon
                            iconoUrl={tipo.iconoUrl}
                            color={tipo.colorIdentificacion}
                            className="w-5 h-5 flex-shrink-0"
                            alt={tipo.nombre}
                          />
                        )}
                        <span className={`${selected ? 'font-semibold text-primary' : 'font-normal text-gray-900'}`}>
                          {tipo.nombre}
                        </span>
                      </div>
                      {selected && <CheckIcon className="w-5 h-5 text-primary flex-shrink-0" />}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </div>
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}

const EventoDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [comentarioTexto, setComentarioTexto] = useState('')
  const [imagenAmpliada, setImagenAmpliada] = useState(null)
  const [loadingImagen, setLoadingImagen] = useState(false)
  const [editandoDescripcion, setEditandoDescripcion] = useState(false)
  const [descripcionTemporal, setDescripcionTemporal] = useState('')
  const [mostrarModalEliminar, setMostrarModalEliminar] = useState(false)
  const [tiempoAbierto, setTiempoAbierto] = useState('')

  // Guardar la lista de eventos en el momento de la carga inicial para navegación
  const [eventosNavegacion, setEventosNavegacion] = useState(null)
  const [totalEventosNavegacion, setTotalEventosNavegacion] = useState(0)

  // Filtros del store para navegación
  const { estadoEvento, idCentroTrabajo } = useFiltersStore()

  // Queries
  const { data: eventoData, isLoading } = useEvento(id)
  const { data: tiposAnomaliaData } = useTiposAnomalia()

  // Cargar la lista de eventos con los filtros actuales para la navegación
  const { data: eventosListData } = useEventos({
    estadoEvento,
    idCentroTrabajo,
    page: 1,
    pageSize: 1000, // Cargar muchos eventos para la navegación
  })
  const { data: imagenesData } = useQuery({
    queryKey: ['imagenes', id],
    queryFn: () => getImagenesEvento(id),
    enabled: !!id,
  })
  const { data: comentariosData } = useQuery({
    queryKey: ['comentarios', id],
    queryFn: () => getComentarios(id),
    enabled: !!id,
  })
  const { data: historialData } = useEventoHistorial(id)

  // Mutations
  const updateEventoMutation = useUpdateEvento()
  const deleteEventoMutation = useDeleteEvento()
  const createComentarioMutation = useMutation({
    mutationFn: (texto) => createComentario(id, { comentario: texto }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comentarios', id] })
      queryClient.invalidateQueries({ queryKey: ['evento', id] })
      setComentarioTexto('')
      toast.success('Comentario añadido')
    },
  })

  // La API devuelve { code, payload, message }
  const evento = eventoData?.payload?.evento
  const tiposAnomalia = tiposAnomaliaData?.payload || []
  const imagenes = imagenesData?.payload || []
  const comentarios = comentariosData?.payload || []
  const historial = historialData?.payload?.historial || []

  // Navegación entre eventos - usar la lista guardada en el estado
  const eventosLista = eventosNavegacion || []
  const currentIndex = eventosLista.findIndex(e => e.idEvento === parseInt(id))
  const totalEventos = totalEventosNavegacion || 0
  const posicionActual = currentIndex >= 0 ? currentIndex + 1 : 0

  const navegarAEvento = (nuevoId) => {
    if (nuevoId) {
      navigate(`/eventos/${nuevoId}`)
    }
  }

  const eventoAnterior = currentIndex > 0 ? eventosLista[currentIndex - 1]?.idEvento : null
  const eventoSiguiente = currentIndex >= 0 && currentIndex < eventosLista.length - 1 ? eventosLista[currentIndex + 1]?.idEvento : null
  const primerEvento = eventosLista.length > 0 ? eventosLista[0]?.idEvento : null
  const ultimoEvento = eventosLista.length > 0 ? eventosLista[eventosLista.length - 1]?.idEvento : null

  // Capturar la lista de eventos en el momento de la carga inicial
  // Solo actualizar cuando cambia el ID o cuando los datos llegan por primera vez
  useEffect(() => {
    if (eventosListData?.payload?.Eventos && !eventosNavegacion) {
      setEventosNavegacion(eventosListData.payload.Eventos)
      setTotalEventosNavegacion(eventosListData.payload.TotalItems)
    }
  }, [eventosListData, eventosNavegacion])

  // Resetear la navegación cuando cambia el ID (al navegar a otro evento)
  useEffect(() => {
    setEventosNavegacion(null)
    setTotalEventosNavegacion(0)
  }, [id])

  // Inicializar descripción temporal cuando se carga el evento
  useEffect(() => {
    if (evento?.descripcion) {
      setDescripcionTemporal(evento.descripcion)
    }
  }, [evento?.descripcion])

  // Calcular y actualizar el tiempo que lleva abierto el evento
  useEffect(() => {
    if (!evento || !historial) return

    const calcularTiempo = () => {
      const fechaInicio = parseUtcDate(evento.fechaCreacionUtc)
      if (!fechaInicio) return

      // Si está resuelto o descartado, buscar la fecha de cierre en el historial
      if (evento.estadoEvento === ESTADOS.RESUELTO || evento.estadoEvento === ESTADOS.DESCARTADO) {
        // Buscar el último cambio a Resuelto o Descartado
        const cambiosCierre = historial.filter(h =>
          h.descripcion?.toLowerCase().includes('resuelto') ||
          h.descripcion?.toLowerCase().includes('descartado')
        )

        if (cambiosCierre.length > 0) {
          // Ordenar por fecha descendente y tomar el más reciente
          const ultimoCierre = cambiosCierre.sort((a, b) =>
            new Date(parseUtcDate(b.fechaCambioUtc)) - new Date(parseUtcDate(a.fechaCambioUtc))
          )[0]

          const fechaCierre = parseUtcDate(ultimoCierre.fechaCambioUtc)
          setTiempoAbierto(formatDuration(fechaInicio, fechaCierre))
        } else {
          // Si no se encuentra en el historial, usar ahora
          setTiempoAbierto(formatDuration(fechaInicio))
        }
      } else {
        // Si está abierto, calcular desde creación hasta ahora
        setTiempoAbierto(formatDuration(fechaInicio))
      }
    }

    // Calcular inmediatamente
    calcularTiempo()

    // Si el evento está abierto, actualizar cada segundo
    let interval
    if (evento.estadoEvento !== ESTADOS.RESUELTO && evento.estadoEvento !== ESTADOS.DESCARTADO) {
      interval = setInterval(calcularTiempo, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [evento, historial])

  // Auto-cambio de NUEVO a VISTO cuando se visualiza el evento
  useEffect(() => {
    if (evento && evento.estadoEvento === ESTADOS.NUEVO) {
      // Pequeño delay para asegurar que la UI se ha renderizado
      const timer = setTimeout(() => {
        updateEventoMutation.mutate({
          id,
          data: { estadoEvento: ESTADOS.VISTO },
        })
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [evento?.idEvento]) // Solo ejecutar cuando cambia el ID del evento (al cargar)

  // Cerrar modal con tecla ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (imagenAmpliada) {
          setImagenAmpliada(null)
        } else if (mostrarModalEliminar) {
          setMostrarModalEliminar(false)
        }
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [imagenAmpliada, mostrarModalEliminar])

  const handleEstadoChange = (nuevoEstado) => {
    const estadoAnterior = evento.estadoEvento
    const estadoParsed = parseInt(nuevoEstado)

    // Si se está cambiando a RESUELTO, lanzar confeti INMEDIATAMENTE
    if (estadoParsed === ESTADOS.RESUELTO && estadoAnterior !== ESTADOS.RESUELTO) {
      // Configuración del confeti
      const duration = 3000
      const animationEnd = Date.now() + duration
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 }

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min
      }

      const interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now()

        if (timeLeft <= 0) {
          return clearInterval(interval)
        }

        const particleCount = 50 * (timeLeft / duration)

        // Lanzar desde dos puntos laterales
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        })
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        })
      }, 250)
    }

    // Actualizar el estado en la BD
    updateEventoMutation.mutate({
      id,
      data: { estadoEvento: estadoParsed },
    }, {
      onSuccess: () => {
        // Si se resolvió el evento, volver a la lista de eventos
        if (estadoParsed === ESTADOS.RESUELTO && estadoAnterior !== ESTADOS.RESUELTO) {
          // Pequeño delay para que se vea el confeti antes de navegar
          setTimeout(() => {
            navigate('/eventos')
          }, 500)
        }
      }
    })
  }

  const handleGravedadChange = (nuevaGravedad) => {
    updateEventoMutation.mutate({
      id,
      data: { severidad: parseInt(nuevaGravedad) },
    })
  }

  const handleTipoAnomaliaChange = (nuevoTipo) => {
    updateEventoMutation.mutate({
      id,
      data: { idTipoAnomalia: parseInt(nuevoTipo) },
    })
  }

  const handleGuardarDescripcion = () => {
    if (descripcionTemporal.trim() !== evento.descripcion) {
      updateEventoMutation.mutate({
        id,
        data: { descripcion: descripcionTemporal.trim() },
      })
    }
    setEditandoDescripcion(false)
  }

  const handleCancelarEdicionDescripcion = () => {
    setDescripcionTemporal(evento.descripcion || '')
    setEditandoDescripcion(false)
  }

  const handleEliminarEvento = () => {
    deleteEventoMutation.mutate(id, {
      onSuccess: () => {
        navigate('/eventos')
      },
    })
  }

  const handleAmpiliarImagen = async (idEventoImagen) => {
    setLoadingImagen(true)
    try {
      const response = await getImagenCompleta(idEventoImagen)
      const imagen = response.payload

      const mimeType = getMimeTypeFromExtension(imagen.extension)
      setImagenAmpliada({
        ...imagen,
        imagenBase64: addBase64Prefix(imagen.imagenBase64, mimeType)
      })
    } catch (error) {
      toast.error('Error al cargar la imagen')
    } finally {
      setLoadingImagen(false)
    }
  }

  const handleSubmitComentario = (e) => {
    e.preventDefault()
    if (comentarioTexto.trim()) {
      createComentarioMutation.mutate(comentarioTexto)
    }
  }

  if (isLoading) return <Loading message="Cargando detalle del evento..." />

  if (!evento) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Evento no encontrado</h2>
          <p className="text-gray-600 mb-6">El evento que buscas no existe o ha sido eliminado.</p>
          <button onClick={() => navigate('/eventos')} className="btn-primary">
            Volver a la lista
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header con botón volver y botón eliminar */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/eventos')}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-primary transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span className="font-medium">Volver</span>
        </button>
        <button
          onClick={() => setMostrarModalEliminar(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
        >
          <TrashIcon className="w-5 h-5" />
          <span className="font-medium">Eliminar evento</span>
        </button>
      </div>

      {/* Navegación entre eventos */}
      {eventosLista.length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            {/* Posición actual */}
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <span className="text-gray-500">Incidencia</span>
              <span className="px-2.5 py-1 bg-primary/10 text-primary rounded-lg font-semibold">
                {posicionActual}
              </span>
              <span className="text-gray-500">de</span>
              <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg font-semibold">
                {totalEventos}
              </span>
            </div>

            {/* Botones de navegación */}
            <div className="flex items-center gap-2">
              {/* Primero */}
              <button
                onClick={() => navegarAEvento(primerEvento)}
                disabled={!eventoAnterior || currentIndex === 0}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Primera incidencia"
              >
                <ChevronDoubleLeftIcon className="w-5 h-5 text-gray-700" />
              </button>

              {/* Anterior */}
              <button
                onClick={() => navegarAEvento(eventoAnterior)}
                disabled={!eventoAnterior}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Incidencia anterior"
              >
                <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
              </button>

              {/* Siguiente */}
              <button
                onClick={() => navegarAEvento(eventoSiguiente)}
                disabled={!eventoSiguiente}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Siguiente incidencia"
              >
                <ChevronRightIcon className="w-5 h-5 text-gray-700" />
              </button>

              {/* Último */}
              <button
                onClick={() => navegarAEvento(ultimoEvento)}
                disabled={!eventoSiguiente || currentIndex === eventosLista.length - 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Última incidencia"
              >
                <ChevronDoubleRightIcon className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tarjeta principal del evento */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Header de la tarjeta con degradado dinámico según severidad */}
        <div className={`bg-gradient-to-r ${SEVERIDADES_HEADER_GRADIENTS[evento.severidad] || 'from-primary to-primary-dark'} p-8 text-white shadow-lg`}>
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <TipoAnomaliaSelector
                value={evento.tipoAnomalia?.idTipoAnomalia}
                onChange={handleTipoAnomaliaChange}
                disabled={updateEventoMutation.isPending}
                tiposAnomalia={tiposAnomalia}
              />
              {/* Tiempo abierto */}
              {tiempoAbierto && (
                <div className="flex items-center gap-2 mt-3 text-white/90">
                  <ClockIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {evento.estadoEvento === ESTADOS.RESUELTO || evento.estadoEvento === ESTADOS.DESCARTADO
                      ? `Estuvo abierto: ${tiempoAbierto}`
                      : `Tiempo abierto: ${tiempoAbierto}`
                    }
                  </span>
                </div>
              )}
            </div>
            <EstadoSelector
              value={evento.estadoEvento}
              onChange={handleEstadoChange}
              disabled={updateEventoMutation.isPending}
            />
          </div>

          {/* Metadatos principales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <MapPinIcon className="w-5 h-5 opacity-80" />
              <div>
                <div className="opacity-80">Ubicación</div>
                <div className="font-semibold">
                  {evento.ubicacion?.centroTrabajo} - {evento.ubicacion?.invernadero}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 opacity-80" />
              <div>
                <div className="opacity-80">Creado el</div>
                <div className="font-semibold">{formatDate(evento.fechaCreacionUtc, 'dd/MM/yyyy HH:mm', true)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <UserCircleIcon className="w-5 h-5 opacity-80" />
              <div>
                <div className="opacity-80">Reportado por</div>
                <div className="font-semibold">{evento.usuarioCreador?.nombre || 'Desconocido'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Cuerpo de la tarjeta */}
        <div className="p-8 space-y-6">
          {/* Gravedad */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Gravedad</label>
            <GravedadSelector
              value={evento.severidad}
              onChange={handleGravedadChange}
              disabled={updateEventoMutation.isPending}
            />
          </div>

          {/* Descripción editable */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">Descripción</label>
              {!editandoDescripcion && (
                <button
                  onClick={() => setEditandoDescripcion(true)}
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary-dark transition-colors font-medium"
                >
                  <PencilIcon className="w-4 h-4" />
                  Editar
                </button>
              )}
            </div>
            {editandoDescripcion ? (
              <div className="space-y-3">
                <textarea
                  value={descripcionTemporal}
                  onChange={(e) => setDescripcionTemporal(e.target.value)}
                  className="textarea"
                  rows="4"
                  placeholder="Escribe una descripción..."
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleGuardarDescripcion}
                    disabled={updateEventoMutation.isPending}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <CheckIcon className="w-4 h-4" />
                    <span>Guardar</span>
                  </button>
                  <button
                    onClick={handleCancelarEdicionDescripcion}
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    <span>Cancelar</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 min-h-[4rem] hover:border-gray-300 transition-colors">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {evento.descripcion || 'Sin descripción'}
                </p>
              </div>
            )}
          </div>

          {/* Notas adicionales */}
          {evento.notasAdicionales && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notas Adicionales</label>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{evento.notasAdicionales}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Galería de imágenes */}
      {imagenes.length > 0 && (
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <PhotoIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Imágenes</h2>
            <span className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold">
              {imagenes.length}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {imagenes.map((imagen) => {
              const mimeType = getMimeTypeFromExtension(imagen.extension)
              const miniaturaConPrefijo = addBase64Prefix(imagen.miniatura, mimeType)

              return (
                <div
                  key={imagen.idEventoImagen}
                  onClick={() => handleAmpiliarImagen(imagen.idEventoImagen)}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer bg-gray-100 shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  {miniaturaConPrefijo ? (
                    <img
                      src={miniaturaConPrefijo}
                      alt={imagen.nombreArchivo}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <PhotoIcon className="w-12 h-12" />
                    </div>
                  )}
                  {/* Overlay al hover */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Comentarios */}
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <ChatBubbleLeftIcon className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Comentarios</h2>
          <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold">
            {comentarios.length}
          </span>
        </div>

        {/* Formulario nuevo comentario */}
        <form onSubmit={handleSubmitComentario} className="mb-8">
          <textarea
            value={comentarioTexto}
            onChange={(e) => setComentarioTexto(e.target.value)}
            className="textarea"
            rows="3"
            placeholder="Escribe un comentario..."
            disabled={createComentarioMutation.isPending}
          />
          <div className="flex justify-end mt-3">
            <button
              type="submit"
              disabled={!comentarioTexto.trim() || createComentarioMutation.isPending}
              className="btn-primary"
            >
              {createComentarioMutation.isPending ? 'Enviando...' : 'Añadir Comentario'}
            </button>
          </div>
        </form>

        {/* Lista de comentarios */}
        <div className="space-y-4">
          {comentarios.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ChatBubbleLeftIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay comentarios todavía</p>
            </div>
          ) : (
            comentarios.map((comentario) => (
              <div key={comentario.idComentario} className="bg-gradient-to-r from-gray-50 to-white rounded-lg p-5 border-l-4 border-primary shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-full">
                      <UserCircleIcon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-semibold text-gray-900">
                      {comentario.usuario?.nombre || 'Usuario'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 font-medium">
                    {formatDate(comentario.fechaCreacion)}
                  </span>
                </div>
                <p className="text-gray-700 ml-9 leading-relaxed">{comentario.comentario}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Trazabilidad */}
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <ClockIcon className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Trazabilidad</h2>
          <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold">
            {historial.length}
          </span>
        </div>

        {/* Lista de cambios */}
        <div className="space-y-3">
          {historial.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClockIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay cambios registrados</p>
            </div>
          ) : (
            historial.map((cambio) => (
              <div key={cambio.idHistorial} className="bg-gradient-to-r from-blue-50/50 to-white rounded-lg p-5 border-l-4 border-blue-400/50 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="p-1.5 bg-blue-100 rounded-full flex-shrink-0 mt-0.5">
                      <UserCircleIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="font-semibold text-gray-900 cursor-help"
                          title={cambio.usuario?.email || 'Sin email'}
                        >
                          {cambio.usuario?.nombre || 'Usuario'}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm break-words leading-relaxed">{cambio.descripcion}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0 font-medium">
                    <CalendarIcon className="w-4 h-4" />
                    <span className="whitespace-nowrap">
                      {formatDate(cambio.fechaCambioUtc, 'dd/MM/yyyy HH:mm', true)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Loading overlay cuando se carga imagen */}
      {loadingImagen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3">
            <div className="spinner h-8 w-8" />
            <p className="text-gray-700">Cargando imagen completa...</p>
          </div>
        </div>
      )}

      {/* Modal de imagen ampliada */}
      <Transition appear show={imagenAmpliada !== null && !loadingImagen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setImagenAmpliada(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-90" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative max-w-6xl transform transition-all">
                  <button
                    onClick={() => setImagenAmpliada(null)}
                    className="absolute -top-12 right-0 text-white bg-black/50 rounded-full p-3 hover:bg-black/70 transition-colors"
                    title="Cerrar (Esc)"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>

                  {imagenAmpliada && (
                    <>
                      <img
                        src={imagenAmpliada.imagenBase64}
                        alt={imagenAmpliada.nombreArchivo}
                        className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
                        <p className="text-white text-sm font-medium">{imagenAmpliada.nombreArchivo}</p>
                        <p className="text-white/80 text-xs">
                          {(imagenAmpliada.tamanoBytes / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal de confirmación de eliminación */}
      <Transition appear show={mostrarModalEliminar} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setMostrarModalEliminar(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Eliminar evento
                      </Dialog.Title>
                      <p className="text-sm text-gray-500 mt-1">
                        Esta acción no se puede deshacer
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6">
                    ¿Estás seguro de que quieres eliminar este evento? Se eliminarán todas las imágenes, comentarios y datos asociados.
                  </p>

                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setMostrarModalEliminar(false)}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleEliminarEvento}
                      disabled={deleteEventoMutation.isPending}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
                    >
                      {deleteEventoMutation.isPending ? (
                        <>
                          <div className="spinner h-4 w-4 border-white" />
                          Eliminando...
                        </>
                      ) : (
                        <>
                          <TrashIcon className="w-4 h-4" />
                          Eliminar
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default EventoDetail
